import ApiService from '../api'

class ForumService {
  /**
   * Get working groups with ServiceResponse handling
   * @returns {Promise}
   */
  async getWorkingGroups() {
    try {
      const response = await ApiService.get('/forums/working-groups', {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'working groups');
    } catch (error) {
      console.error('❌ ForumService: Error in getWorkingGroups:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get forums with ServiceResponse handling
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getForums(params = {}) {
    try {
      const response = await ApiService.get('/forums', {
        params,
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'forums');
    } catch (error) {
      console.error('❌ ForumService: Error in getForums:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get a specific forum by ID
   * @param {string} id - Forum ID
   * @returns {Promise}
   */
  async getForum(id) {
    try {
      const response = await ApiService.get(`/forums/${id}`, {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'forum');
    } catch (error) {
      console.error('❌ ForumService: Error in getForum:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get forum analytics
   * @returns {Promise}
   */
  async getAnalytics() {
    try {
      const response = await ApiService.get('/forums/analytics', {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'analytics');
    } catch (error) {
      console.error('❌ ForumService: Error in getAnalytics:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create a new forum
   * @param {Object} forumData - Forum data
   * @returns {Promise}
   */
  async createForum(payload) {
    try {
      // ✅ Pass {} for headers (2nd param), payload for parameters (3rd param)
      const response = await ApiService.post('/forums', {}, payload);

      // `ApiService.post` returns a ServiceResponse object
      if (response.Success === false) {
        console.error('❌ Forum creation failed:', response.Message, response.Data || '');
        throw response;
      }

      // Standardize return format for Vue components
      return {
        success: true,
        data: response.Data?.data || response.Data || null,
        message: response.Message || 'Forum created successfully',
      };

    } catch (error) {
      console.error('❌ ForumService.createForum error:', error);
      throw error;
    }
  }


  /**
   * Update an existing forum
   * @param {string} id - Forum ID
   * @param {Object} forumData - Updated forum data
   * @returns {Promise}
   */
  async updateForum(id, forumData) {
    try {
      const cleanData = this.validateForumData(forumData);
      const response = await ApiService.put(`/forums/${id}`, {}, forumData)
      return this.handleServiceResponse(response, 'update forum');
    } catch (error) {
      console.error('❌ ForumService: Error in updateForum:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete a forum
   * @param {string} id - Forum ID
   * @returns {Promise}
   */
  async deleteForum(id) {
    try {
      const response = await ApiService.delete(`/forums/${id}`, {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'delete forum');
    } catch (error) {
      console.error('❌ ForumService: Error in deleteForum:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get topics for a specific forum
   * @param {string} forumId - Forum ID
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getForumTopics(forumId, params = {}) {
    try {
      const response = await ApiService.get(`/forums/${forumId}/topics`, {
        params,
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'forum topics');
    } catch (error) {
      console.error('❌ ForumService: Error in getForumTopics:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get a specific topic by ID
   * @param {string} topicId - Topic ID
   * @returns {Promise}
   */
  async getTopic(topicId) {
    try {
      const response = await ApiService.get(`/topics/${topicId}`, {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'topic');
    } catch (error) {
      console.error('❌ ForumService: Error in getTopic:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create a new topic in a forum
   * @param {string} forumId - Forum ID
   * @param {Object} topicData - Topic data
   * @returns {Promise}
   */
  async createTopic(forumId, topicData) {
    try {
      const response = await ApiService.post(`/forums/${forumId}/topics`, {}, topicData);
      return this.handleServiceResponse(response, 'create topic');
    } catch (error) {
      console.error('❌ ForumService: Error in createTopic:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update a topic
   * @param {string} topicId - Topic ID
   * @param {Object} topicData - Updated topic data
   * @returns {Promise}
   */
  async updateTopic(topicId, topicData) {
    try {
      const cleanData = this.validateTopicData(topicData);
      const response = await ApiService.put(`/topics/${topicId}`, {}, cleanData)
      return this.handleServiceResponse(response, 'update topic');
    } catch (error) {
      console.error('❌ ForumService: Error in updateTopic:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete a topic
   * @param {string} topicId - Topic ID
   * @returns {Promise}
   */
  async deleteTopic(topicId) {
    try {
      const response = await ApiService.delete(`/topics/${topicId}`, {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'delete topic');
    } catch (error) {
      console.error('❌ ForumService: Error in deleteTopic:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get comments for a specific topic
   * @param {string} topicId - Topic ID
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getTopicComments(topicId, params = {}) {
    try {
      const response = await ApiService.get(`/topics/${topicId}/comments`, {
        params,
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'topic comments');
    } catch (error) {
      console.error('❌ ForumService: Error in getTopicComments:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create a new comment on a topic
   * @param {string} topicId - Topic ID
   * @param {Object} commentData - Comment data
   * @returns {Promise}
   */
  async createComment(topicId, commentData) {
    try {
      const cleanData = this.validateCommentData(commentData);
      const response = await ApiService.post(`/topics/${topicId}/comments`, {}, cleanData)
      return this.handleServiceResponse(response, 'create comment');
    } catch (error) {
      console.error('❌ ForumService: Error in createComment:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update a comment
   * @param {string} commentId - Comment ID
   * @param {Object} commentData - Updated comment data
   * @returns {Promise}
   */
  async updateComment(commentId, commentData) {
    try {
      const cleanData = this.validateCommentData(commentData);
      const response = await ApiService.put(`/comments/${commentId}`, {}, cleanData)
      return this.handleServiceResponse(response, 'update comment');
    } catch (error) {
      console.error('❌ ForumService: Error in updateComment:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete a comment
   * @param {string} commentId - Comment ID
   * @returns {Promise}
   */
  async deleteComment(commentId) {
    try {
      const response = await ApiService.delete(`/comments/${commentId}`, {
        timeout: 10000
      });
      return this.handleServiceResponse(response, 'delete comment');
    } catch (error) {
      console.error('❌ ForumService: Error in deleteComment:', error);
      return this.handleError(error);
    }
  }

  async getPendingComments() {
    try {
      const response = await ApiService.get('/forums/comments/pending', { timeout: 10000 });

      // Use your service response normalizer
      const normalized = this.handleServiceResponse(response, 'pending comments');

      // If normalized.data is nested (object with .data), unwrap:
      if (normalized?.data && typeof normalized.data === 'object' && Array.isArray(normalized.data.data)) {
        normalized.data = normalized.data.data;
      }

      // If someone accidentally double-wrapped (Data/Data), try to unwrap again:
      if (normalized?.data && typeof normalized.data === 'object' && normalized.data.data && Array.isArray(normalized.data.data)) {
        normalized.data = normalized.data.data;
      }

      // Ensure data is array
      normalized.data = Array.isArray(normalized.data) ? normalized.data : [];

      return normalized;
    } catch (error) {
      console.error('❌ ForumService.getPendingComments error:', error);
      return this.handleError(error);
    }
  }

  approveComment(commentId) {
    return ApiService.post(`/forums/comments/${commentId}/approve`)
  }

  rejectComment(commentId) {
    return ApiService.post(`/forums/comments/${commentId}/reject`)
  }

  /**
   * Validate and clean forum data
   * @private
   * @param {Object} data - Raw forum data
   * @returns {Object} - Cleaned forum data
   */
  validateForumData(data) {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid forum data provided');
    }

    // Required fields validation
    if (!data.subject || typeof data.subject !== 'string' || data.subject.trim().length < 3) {
      throw new Error('Forum subject is required and must be at least 3 characters');
    }

    if (!data.description || typeof data.description !== 'string' || data.description.trim().length < 10) {
      throw new Error('Forum description is required and must be at least 10 characters');
    }

    if (!data.working_group_id) {
      throw new Error('Working group is required');
    }

    // Clean and validate data
    const cleanData = {
      subject: data.subject.toString().trim(),
      description: data.description.toString().trim(),
      working_group_id: data.working_group_id,
      enable_mod: Boolean(data.enable_mod)
    };

    // Ensure working_group_id is properly formatted
    if (typeof cleanData.working_group_id === 'string') {
      // If it's a UUID string, keep it as string
      cleanData.working_group_id = cleanData.working_group_id.trim();
    } else if (typeof cleanData.working_group_id === 'number') {
      // If it's a number, convert to string for consistency
      cleanData.working_group_id = cleanData.working_group_id.toString();
    }

    return cleanData;
  }

  /**
   * Validate and clean topic data
   * @private
   * @param {Object} data - Raw topic data
   * @returns {Object} - Cleaned topic data
   */
  validateTopicData(data) {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid topic data provided');
    }

    // Required fields validation
    if (!data.title || typeof data.title !== 'string' || data.title.trim().length < 3) {
      throw new Error('Topic title is required and must be at least 3 characters');
    }

    if (!data.content || typeof data.content !== 'string' || data.content.trim().length < 10) {
      throw new Error('Topic content is required and must be at least 10 characters');
    }

    // Clean and validate data
    const cleanData = {
      title: data.title.toString().trim(),
      content: data.content.toString().trim(),
      priority: (data.priority || 'low').toString().toLowerCase(),
      is_sticky: Boolean(data.is_sticky),
      is_locked: Boolean(data.is_locked)
    };

    // Validate priority
    if (!['low', 'medium', 'high'].includes(cleanData.priority)) {
      cleanData.priority = 'low';
    }

    return cleanData;
  }

  /**
   * Validate and clean comment data
   * @private
   * @param {Object} data - Raw comment data
   * @returns {Object} - Cleaned comment data
   */
  validateCommentData(data) {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid comment data provided');
    }

    // Required fields validation
    if (!data.content || typeof data.content !== 'string' || data.content.trim().length < 5) {
      throw new Error('Comment content is required and must be at least 5 characters');
    }

    // Clean and validate data
    const cleanData = {
      content: data.content.toString().trim()
    };

    return cleanData;
  }

  /**
   * Test API connectivity
   * @returns {Promise}
   */
  async testConnection() {
    try {
      const response = await ApiService.get('/forums/working-groups', {
        timeout: 5000
      });
      return this.handleServiceResponse(response, 'connection test');
    } catch (error) {
      console.error('❌ ForumService: Connection test failed:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle ServiceResponse objects specifically
   * @private
   * @param {Object} response - The response from API
   * @param {string} operation - Operation name for logging
   * @returns {Object} - Normalized response
   */
  handleServiceResponse(response, operation = 'request') {
    if (!response || typeof response !== 'object') {
      console.warn(`⚠️ ${operation} returned invalid response`, response);
      return { success: false, message: 'Invalid response format', data: [] };
    }

    // ✅ Case 1: New Laravel API format
    if ('success' in response && 'data' in response) {
      return {
        success: response.success,
        data: response.data,
        message: response.message || 'Request successful',
        status: response.status || 200,
        errors: response.errors || {}
      };
    }

    // ✅ Case 2: Old ServiceResponse format (Admin side)
    if ('Data' in response && 'Success' in response) {
      return {
        success: response.Success,
        data: response.Data,
        message: response.Message || 'Request successful',
        status: response.Status || 200,
        errors: response.Errors || {}
      };
    }

    // ✅ Case 3: Raw Axios response
    if ('data' in response && typeof response.data === 'object') {
      const resData = response.data;
      return {
        success: resData.success ?? true,
        data: resData.data ?? resData,
        message: resData.message || 'Request successful',
        status: response.status || 200,
        errors: resData.errors || {}
      };
    }

    console.warn(`⚠️ ${operation}: unrecognized response structure`, response);
    return { success: false, data: [], message: 'Unexpected response format' };
  }

  /**
   * Handle API errors
   * @private
   * @param {Error} error - The error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    console.error('🚨 ForumService: Handling error...');
    console.error('Error details:', {
      message: error.message,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      name: error.name
    });

    if (error.response) {
      // Server responded with error status
      const { status, data, statusText } = error.response;
      console.error('Server error details:', { status, statusText, data });

      let errorMessage = 'Request failed';
      let errorData = null;
      let errors = {};

      // Handle ServiceResponse error format
      if (data && typeof data === 'object') {
        // Check for ServiceResponse format first
        if (data.Success === false && data.Message) {
          errorMessage = data.Message;
          errorData = data.Data || null;
          errors = data.Errors || {};
        }
        // Check for Laravel validation error format
        else if (data.message) {
          errorMessage = data.message;
          errorData = data.data || null;
          errors = data.errors || {};
        }
        // Check for direct error field
        else if (data.error) {
          errorMessage = data.error;
          errors = data.errors || {};
        }
        // Use status text as fallback
        else if (statusText) {
          errorMessage = statusText;
        }
      }

      return {
        success: false,
        status,
        message: errorMessage,
        errors: errors,
        data: errorData
      };
    } else if (error.request) {
      // Network error
      console.error('Network error - no response received');
      return {
        success: false,
        status: 0,
        message: 'Network error. Please check your internet connection.',
        errors: {},
        data: null
      };
    } else {
      // Request setup error
      console.error('Request setup error:', error.message);
      return {
        success: false,
        status: 500,
        message: error.message || 'An unexpected error occurred',
        errors: {},
        data: null
      };
    }
  }
}

export default new ForumService();
